"use client";

import { useCallback } from "react";
import { create } from "zustand";

import routes from "@/lib/routes";
import { StateConfig } from "@/lib/states-config";

const PRESERVED_FILTERS_STORAGE_KEY = "preservedJobFilters";

export type KeyValueStorage = {
  get: (key: string) => string | null;
  set: (key: string, value: string) => void;
  remove: (key: string) => void;
};

export const browserSessionStorage: KeyValueStorage = {
  get: (key) => {
    if (typeof sessionStorage === "undefined") return null;
    return sessionStorage.getItem(key);
  },
  set: (key, value) => {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(key, value);
  },
  remove: (key) => {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.removeItem(key);
  },
};

export interface PreservedFiltersStore {
  filters: string | null;
  savePreservedFilters: (queryString: string) => void;
  clearPreservedFilters: () => void;
}

export function createPreservedFiltersStore(storage: KeyValueStorage) {
  const store = create<PreservedFiltersStore>()((set) => ({
    filters: storage.get(PRESERVED_FILTERS_STORAGE_KEY),
    savePreservedFilters: (queryString: string) =>
      set({ filters: queryString }),
    clearPreservedFilters: () => set({ filters: null }),
  }));

  store.subscribe((state) => {
    if (state.filters === null) {
      storage.remove(PRESERVED_FILTERS_STORAGE_KEY);
    } else {
      storage.set(PRESERVED_FILTERS_STORAGE_KEY, state.filters);
    }
  });

  return store;
}

export const usePreservedFiltersStore = createPreservedFiltersStore(
  browserSessionStorage,
);

export function usePreserveFilters() {
  const { filters, savePreservedFilters, clearPreservedFilters } =
    usePreservedFiltersStore();

  const getJobSearchUrl = useCallback(
    (state: StateConfig): string => {
      const jobSearchBaseUrl = routes.getJobsState({ state });
      if (!filters) return jobSearchBaseUrl;

      return `${jobSearchBaseUrl}?${filters}`;
    },
    [filters],
  );

  return {
    savePreservedFilters,
    clearPreservedFilters,
    getPreservedFilters: () => filters,
    getJobSearchUrl,
  };
}
